/**
 * The pages that keep beating the learner, across every read of the book.
 *
 * The report's own list — *worth another look* — is about this sitting: the questions
 * this reading lost. This section is about the others, and it exists because the two
 * are different questions. A learner on their third read does not need to be told
 * again what they got wrong today; they need to be told that page 12 has cost them a
 * question in all three readings, which no single sitting's report can say.
 *
 * Three decisions shape it.
 *
 *  - **It is a list of *pages*, not of questions.** A learner cannot turn back to a
 *    wrong answer; they can turn back to page 12. Every row is a door — the same door
 *    the journey rows below use — so the section is an action rather than a verdict.
 *  - **It appears only when a page has cost something twice** (see
 *    `#/lib/flashbookPageTrouble`, which decides it). Once is today's news and is
 *    already listed above; twice is a pattern, and a section that named a page for
 *    one bad answer would be a second copy of the section above it.
 *  - **It says nothing rather than something wrong.** A book read once has no
 *    pattern to report and no section; a book whose readings are older than the
 *    page-by-page record has only the readings it *does* remember, and the lead
 *    sentence is written round that number rather than round the read count — the
 *    one number a learner would otherwise be right to doubt.
 */
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {type FlashBookPageTrouble} from '#/lib/flashbookPageTrouble'
import {useFlashBookPlayPalette} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  useCopyStyle,
  useReportType,
} from '#/components/flashbooks/useReportChrome'

type Props = {
  trouble: FlashBookPageTrouble
  /** Turn back to a page — the same door the journey rows open. */
  onReviewPage?: (pageIndex: number) => void
}

export function FlashBookTroubledPagesSection({trouble, onReviewPage}: Props) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()

  return (
    <View testID="flashbook-report-trouble" style={styles.section}>
      <Text style={[type.title, copy.text, {color: pal.text}, styles.title]}>
        <Trans>Pages that keep beating you</Trans>
      </Text>

      {/*
        The lead counts the *readings the counts are drawn from*, not the number of
        times the book was read. A learner whose first four readings happened before
        the app kept a page-by-page record has seven reads and three counted ones,
        and a sentence built on seven would be a claim the list below cannot support.
      */}
      <Text
        testID="flashbook-report-trouble-lead"
        style={[type.body, copy.text, {color: pal.body}]}>
        {trouble.pages.length === 0 ? (
          <Trans>
            No page has cost you a question twice across these readings.
          </Trans>
        ) : (
          <Trans>
            Across the readings this device remembers, these pages kept costing
            you questions.
          </Trans>
        )}
      </Text>

      {trouble.pages.map(page => (
        <TouchableOpacity
          key={page.pageId}
          testID={`flashbook-report-trouble--${page.index}`}
          accessibilityRole={onReviewPage ? 'button' : undefined}
          accessibilityLabel={l`Page ${page.index + 1}, lost ${page.lost} questions in ${page.reads} reads`}
          accessibilityHint={
            onReviewPage ? l`Turns back to page ${page.index + 1}` : undefined
          }
          disabled={!onReviewPage}
          onPress={onReviewPage ? () => onReviewPage(page.index) : undefined}
          style={[
            styles.row,
            {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
            copy.row,
          ]}>
          <View style={styles.rowTop}>
            <Text style={[type.label, copy.text, {color: pal.text}]}>
              {l`Page ${page.index + 1}`}
            </Text>
            {page.title ? (
              <Text
                numberOfLines={1}
                style={[
                  type.caption,
                  copy.text,
                  styles.pageTitle,
                  {color: pal.muted},
                ]}>
                {page.title}
              </Text>
            ) : null}
          </View>

          <View style={styles.rowFoot}>
            {/* The count is the claim, so it is the one thing on the row that is
                allowed to be loud: the warning ink the report already uses for what
                needs work. */}
            <Text
              testID={`flashbook-report-trouble-count--${page.index}`}
              style={[type.caption, copy.text, {color: pal.warning}]}>
              <Plural value={page.lost} one="# question" other="# questions" />
              {' · '}
              <Plural value={page.reads} one="# read" other="# reads" />
            </Text>
            <View style={styles.grow} />
            {onReviewPage ? (
              <Feather name="chevron-right" size={14} color={pal.accent} />
            ) : null}
          </View>
        </TouchableOpacity>
      ))}

      {/*
        What the counts are drawn from.

        A number the learner can check is a number they can trust: a book read seven
        times whose first four readings predate the page-by-page record is counted
        over three, and saying so is the difference between an honest section and one
        that quietly under-reports.
      */}
      <Text
        testID="flashbook-report-trouble-reads"
        style={[type.caption, copy.text, {color: pal.muted}]}>
        <Plural
          value={trouble.reads}
          one="Counted over the last # read."
          other="Counted over the last # reads."
        />
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  section: {gap: 8},
  title: {marginBottom: 2},
  row: {borderWidth: 1, borderRadius: 12, padding: 11, gap: 4},
  rowTop: {flexDirection: 'row', alignItems: 'baseline', gap: 7},
  rowFoot: {flexDirection: 'row', alignItems: 'center', gap: 6},
  /** The page's own title, sharing the line with the page number. */
  pageTitle: {flexShrink: 1},
  grow: {flex: 1},
})
